import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Fab, Box, Typography, Backdrop } from '@mui/material';
import {
  Add,
  Assessment,
  PowerSettingsNew,
  PlayArrow,
  Close,
} from '@mui/icons-material';
import { useSessionNavigationStore } from '@store/session-navigation-store';
import { useTranslation } from 'react-i18next';

export function SessionFloatingActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeSessionId } = useSessionNavigationStore();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  // Only show on session-related pages
  const isSessionPage =
    location.pathname.includes('/session') ||
    location.pathname === '/sessions';

  if (!activeSessionId || !isSessionPage) return null;

  const isOnObservePage = location.pathname.includes('/observe');

  const handleNewOpportunity = () => {
    setExpanded(false);
    if (isOnObservePage) {
      // Already on the observe page, just open the recorder
      window.dispatchEvent(new CustomEvent('open-opportunity-recorder'));
    } else {
      // Navigate to observe page and flag the store to open recorder on mount
      useSessionNavigationStore.getState().setPendingRecorderOpen(true);
      navigate(`/session/${activeSessionId}/observe`);
    }
  };

  const actions = [
    {
      icon: <PlayArrow />,
      label: t('floatingActions.activeSession'),
      color: 'primary' as const,
      onClick: () => {
        setExpanded(false);
        navigate(`/session/${activeSessionId}/observe`);
      },
    },
    {
      icon: <Add />,
      label: t('floatingActions.newOpportunity'),
      color: 'primary' as const,
      onClick: handleNewOpportunity,
    },
    {
      icon: <Assessment />,
      label: t('floatingActions.sessionSummary'),
      color: 'primary' as const,
      onClick: () => {
        setExpanded(false);
        navigate(`/session/${activeSessionId}/review`);
      },
    },
    {
      icon: <PowerSettingsNew />,
      label: t('floatingActions.endSession'),
      color: 'error' as const,
      onClick: () => {
        setExpanded(false);
        useSessionNavigationStore.getState().setShowExitConfirmation(true);
      },
    },
  ];

  return (
    <>
      <Backdrop
        open={expanded}
        onClick={() => setExpanded(false)}
        sx={{ zIndex: 1200 }}
      />

      {expanded && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 144,
            right: 16,
            zIndex: 1201,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 1.5,
          }}
        >
          {actions.map((action) => (
            <Box
              key={action.label}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  bgcolor: 'background.paper',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  boxShadow: 1,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {action.label}
              </Typography>
              <Fab
                size="small"
                color={action.color}
                onClick={action.onClick}
              >
                {action.icon}
              </Fab>
            </Box>
          ))}
        </Box>
      )}

      <Fab
        color="primary"
        aria-label={t('floatingActions.sessionActions')}
        onClick={() => setExpanded(!expanded)}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 1201,
        }}
      >
        {expanded ? <Close /> : <Add />}
      </Fab>
    </>
  );
}
